import React, { useEffect, useId, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LenseField } from '../../../services/twinService';
import { editInputKind, formatEditValue, parseEditValue, type ParseResult } from './editValue';

interface Props {
    field: LenseField;
    initialValue: unknown;
    label: string;
    onSave: (value: unknown) => Promise<void>;
    /** 'escape' = keyboard dismissal (caller returns focus to the badge); 'outside' = a click elsewhere. */
    onCancel: (reason: 'escape' | 'outside') => void;
    valueLabels?: Record<string, string>;
}

const REASON_KEY: Record<Exclude<ParseResult, { ok: true }>['reason'], string> = {
    empty: 'farm.edit.errorEmpty',
    not_a_number: 'farm.edit.errorNotANumber',
    not_an_integer: 'farm.edit.errorNotAnInteger',
    invalid_date: 'farm.edit.errorInvalidDate',
    not_allowed: 'farm.edit.errorNotAllowed',
};

/**
 * HOF-626 — one property's editor: input + save button. Enter saves, Escape cancels,
 * a mousedown outside the root cancels (capture phase, so it runs before the other
 * badge's click opens the next editor). Native select/date inputs on purpose: a Radix
 * portal would sit outside the root and read as "outside".
 */
export const InlineValueEditor: React.FC<Props> = ({ field, initialValue, label, onSave, onCancel, valueLabels }) => {
    const { t } = useTranslation();
    const errorId = useId();
    const rootRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
    const kind = editInputKind(field);
    const [text, setText] = useState(() => {
        const initial = formatEditValue(field, initialValue);
        if (kind === 'select' && initial === '' && field.values?.length) return field.values[0];
        if (kind === 'boolean' && initial === '') return 'true';
        return initial;
    });
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const parsed = parseEditValue(field, text);

    useEffect(() => {
        inputRef.current?.focus();
        if (inputRef.current instanceof HTMLInputElement) inputRef.current.select();
    }, []);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) onCancel('outside');
        };
        document.addEventListener('mousedown', onDocMouseDown, true);
        return () => document.removeEventListener('mousedown', onDocMouseDown, true);
    }, [onCancel]);

    const submit = async () => {
        if (!parsed.ok || saving) return;
        setSaving(true);
        setServerError(null);
        try {
            await onSave(parsed.value);
        } catch (err) {
            setServerError(err instanceof Error && err.message ? err.message : t('farm.edit.errorServer'));
            setSaving(false);
            // The input is readOnly (not disabled) while saving, so it is focusable again at once.
            inputRef.current?.focus();
        }
    };
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.nativeEvent.isComposing) return; // Enter confirming an IME candidate is not a submit
        if (e.key === 'Enter') { e.preventDefault(); void submit(); }
        if (e.key === 'Escape') { e.preventDefault(); onCancel('escape'); }
    };
    const error = serverError ?? (parsed.ok ? null : t(REASON_KEY[parsed.reason]));

    return (
        <span ref={rootRef} className="property-grid__editor">
            {kind === 'select' || kind === 'boolean' ? (
                <select ref={inputRef as React.RefObject<HTMLSelectElement>} className="property-grid__editor-input" aria-label={label}
                        value={text} onChange={(e) => { if (!saving) setText(e.target.value); }} onKeyDown={onKeyDown} aria-busy={saving}
                        aria-invalid={!!error} aria-describedby={errorId}>
                    {kind === 'boolean'
                        ? [<option key="t" value="true">{t('farm.edit.yes')}</option>, <option key="f" value="false">{t('farm.edit.no')}</option>]
                        : (field.values ?? []).map((v) => <option key={v} value={v}>{valueLabels?.[v] ?? v}</option>)}
                </select>
            ) : (
                <input ref={inputRef as React.RefObject<HTMLInputElement>} className="property-grid__editor-input" aria-label={label}
                       type={kind === 'date' ? 'date' : 'text'} inputMode={kind === 'number' ? 'decimal' : undefined}
                       value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} readOnly={saving} aria-busy={saving}
                       aria-invalid={!!error} aria-describedby={errorId} />
            )}
            <button type="button" className="property-grid__save" aria-label={t('farm.edit.save')} title={t('farm.edit.save')}
                    onClick={() => void submit()} disabled={!parsed.ok || saving}>
                <Check size={13} aria-hidden />
            </button>
            <span id={errorId} className="property-grid__editor-error" aria-live="polite">{error}</span>
        </span>
    );
};

export default InlineValueEditor;
